import { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { Form, Loading, Modal, Select, SelectItem, SelectSkeleton, TextInput } from '@carbon/react';
import { create, enforce, test } from 'vest';

import { useNotifications } from 'core/notifications';
import { USER_ROLE } from 'core/user/role';
import { useUser } from 'core/user/user';
import { SHARE_LEVEL } from 'models/shareLevel';
import useMutateShareProject from 'shared/api/projects/useMutateShareProject';
import { createUseProjectSharesQueryKey } from 'shared/api/projects/useProjectSharesQuery';
import useUsersQuery from 'shared/api/users/useUsersQuery';
import useForm from 'utils/hooks/useForm';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';

const SHARE_LEVEL_LABEL = {
  [SHARE_LEVEL.view]: 'Can View',
  [SHARE_LEVEL.edit]: 'Can Edit'
};

const STRICT_SHARE_LEVEL_BY_USER = {
  [USER_ROLE.viewer]: SHARE_LEVEL.view,
  [USER_ROLE.manager]: SHARE_LEVEL.edit,
  [USER_ROLE.admin]: SHARE_LEVEL.edit
};

const suite = create((data = {}) => {
  test('userId', 'User is required', () => {
    enforce(data.userId).isNotEmpty();
  });
});

const ShareProjectModal = ({ isOpen, projectId, onClose }) => {
  const queryClient = useQueryClient();

  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const { user } = useUser();

  const { data: nonAdminUsersRaw = [], isLoading: isLoadingNonAdminUsers } = useUsersQuery();
  const nonAdminUsers = useMemo(() => nonAdminUsersRaw.filter((x) => x.id !== user.id), [nonAdminUsersRaw, user]);

  const shareProjectMutation = useMutateShareProject({
    onError: () => showErrorNotification({ description: 'Failed to share the project' }),
    onSuccess: () => {
      onClose();
      queryClient.refetchQueries(createUseProjectSharesQueryKey(projectId));
      showSuccessNotification({ description: 'Project shared successfully' });
    }
  });

  const { reset, handleSubmit, register, watch, resetField } = useForm({
    suite,
    defaultValues: {
      userId: '',
      shareLevel: SHARE_LEVEL.view
    }
  });

  const selectedUserId = Number(watch('userId'));
  const selectedUser = useMemo(() => nonAdminUsers.find((x) => x.id === selectedUserId), [selectedUserId]);

  useUpdateEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen]);

  useUpdateEffect(() => resetField('shareLevel'), [selectedUserId]);

  const handleFormSubmit = (values) => {
    shareProjectMutation.mutate({
      id: projectId,
      share: {
        userId: Number(values.userId),
        shareLevel:
          STRICT_SHARE_LEVEL_BY_USER[selectedUser.role] === undefined
            ? Number(values.shareLevel)
            : STRICT_SHARE_LEVEL_BY_USER[selectedUser.role]
      }
    });
  };

  return (
    <Modal
      open={isOpen}
      size="xs"
      modalHeading="Share Project"
      primaryButtonText="Share"
      secondaryButtonText="Cancel"
      onRequestClose={onClose}
      onRequestSubmit={handleSubmit(handleFormSubmit)}
      onSecondarySubmit={onClose}
    >
      <Loading active={shareProjectMutation.isLoading} />
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        {isLoadingNonAdminUsers ? (
          <SelectSkeleton />
        ) : (
          <Select id="user-id" labelText="User" {...register('userId')}>
            <SelectItem disabled hidden value="" text="Choose a user" />
            {nonAdminUsers.map((x) => (
              <SelectItem key={x.id} value={x.id} text={x.name} />
            ))}
          </Select>
        )}
        <br />
        {isLoadingNonAdminUsers ? (
          <SelectSkeleton />
        ) : !selectedUser || STRICT_SHARE_LEVEL_BY_USER[selectedUser.role] !== undefined ? (
          <TextInput
            id="share-level"
            labelText="Share Level"
            value={SHARE_LEVEL_LABEL[STRICT_SHARE_LEVEL_BY_USER[selectedUser?.role ?? USER_ROLE.viewer]]}
            readOnly
          />
        ) : (
          <Select id="share-level" labelText="Share Level" {...register('shareLevel')}>
            {[SHARE_LEVEL.view, SHARE_LEVEL.edit].map((x) => (
              <SelectItem key={x} value={x} text={SHARE_LEVEL_LABEL[x]} />
            ))}
          </Select>
        )}
      </Form>
    </Modal>
  );
};

export default ShareProjectModal;
